/**
 * API Configuration
 * Auto-detects environment based on hostname
 */

// Hosted sites retain hostname selection. Local use can select one coherent
// backend/auth environment without changing its registered localhost callback.
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isLocal = hostname === 'localhost';
const requestedEnvironment = process.env.REACT_APP_ENVIRONMENT || '';
if (isLocal && requestedEnvironment && !['production', 'development'].includes(requestedEnvironment)) {
  throw new Error('REACT_APP_ENVIRONMENT must be production or development.');
}
if (requestedEnvironment && !isLocal && !['app.mindmeld.dev', 'dev.mindmeld.dev'].includes(hostname)) {
  throw new Error('Explicit local environment selection requires the localhost hostname.');
}
const isProduction = isLocal ? requestedEnvironment === 'production' : hostname === 'app.mindmeld.dev';
const isDevelopment = !isProduction && (hostname === 'dev.mindmeld.dev' || isLocal);

// Environment-specific configuration
const ENV_CONFIG = {
  production: {
    apiUrl: 'https://api.mindmeld.dev',
    userPoolId: 'us-east-2_638OhwuV1',
    clientId: '1al1vftuoh55a8n08ea6kr8heq',
  },
  development: {
    apiUrl: 'https://u16nv4eqib.execute-api.us-east-2.amazonaws.com/dev',
    userPoolId: 'us-east-2_f9PSZ3Fpj',
    clientId: '6uif970sisfpvk5r6vg17uvf8r',
  },
};

const activeConfig = isProduction ? ENV_CONFIG.production : ENV_CONFIG.development;
// Do not silently combine a selected production API with stale dev auth overrides.
if (isLocal && requestedEnvironment) {
  const overrides = [
    [process.env.REACT_APP_API_URL, activeConfig.apiUrl],
    [process.env.REACT_APP_USER_POOL_ID, activeConfig.userPoolId],
    [process.env.REACT_APP_USER_POOL_CLIENT_ID, activeConfig.clientId],
    [process.env.REACT_APP_AWS_REGION, 'us-east-2'],
  ];
  if (overrides.some(([value, expected]) => value && value !== expected)) {
    throw new Error('Local environment selection conflicts with legacy API/Cognito overrides. Remove the conflicting REACT_APP_* overrides.');
  }
}


// API Gateway URL
export const API_BASE_URL = process.env.REACT_APP_API_URL || activeConfig.apiUrl;

// Cognito Configuration
export const COGNITO_CONFIG = {
  region: process.env.REACT_APP_AWS_REGION || 'us-east-2',
  userPoolId: process.env.REACT_APP_USER_POOL_ID || activeConfig.userPoolId,
  clientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || activeConfig.clientId,
  domain: isProduction ? 'mindmeld-auth.auth.us-east-2.amazoncognito.com' : 'mindmeld-users.auth.us-east-2.amazoncognito.com',
  googleEnabled: isProduction,
  redirectUri: isLocal ? 'http://localhost:9876/callback' : isProduction ? 'https://app.mindmeld.dev/callback' : hostname === 'dev.mindmeld.dev' ? 'https://dev.mindmeld.dev/callback' : 'http://localhost:9876/callback',
};

// Export environment info for debugging
export const ENVIRONMENT = {
  isProduction,
  isDevelopment,
  hostname,
  isLocal,
};

// Local dev and production share a browser origin. Reject credentials from the
// other pool/client before either headers or refresh/session reads can use them.
// This is a routing boundary, not signature verification or authorization.
export function ensureSessionEnvironment(): boolean {
  if (!isLocal || typeof localStorage === 'undefined') return true;
  const issuer = `https://cognito-idp.${COGNITO_CONFIG.region}.amazonaws.com/${COGNITO_CONFIG.userPoolId}`;
  const decode = (token: string | null) => {
    if (!token) return null;
    try { return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); }
    catch { return null; }
  };
  const id = decode(localStorage.getItem('mindmeld_token'));
  const access = decode(localStorage.getItem('mindmeld_access_token'));
  const matches = Boolean(id && access && id.iss === issuer && access.iss === issuer &&
    id.aud === COGNITO_CONFIG.clientId && access.client_id === COGNITO_CONFIG.clientId &&
    id.token_use === 'id' && access.token_use === 'access' && typeof id.sub === 'string' && id.sub && id.sub === access.sub);
  if (!matches) {
    for (const key of ['mindmeld_token', 'mindmeld_access_token', 'mindmeld_refresh_token', 'mindmeld_email', 'mindmeld_auth_method']) localStorage.removeItem(key);
  }
  if (typeof sessionStorage !== 'undefined') {
    const raw = sessionStorage.getItem('mindmeld_oauth_pending');
    if (raw) {
      let pending;
      try { pending = JSON.parse(raw); } catch { pending = null; }
      if (!pending || pending.issuer !== issuer || pending.clientId !== COGNITO_CONFIG.clientId) {
        sessionStorage.removeItem('mindmeld_oauth_pending');
        sessionStorage.removeItem('mindmeld_oauth_exchange');
      }
    }
  }
  return matches;
}
ensureSessionEnvironment();

/**
 * Get authorization header from stored token
 */
export const getAuthHeader = (): Record<string, string> => {
  if (!ensureSessionEnvironment()) return {};
  const token = localStorage.getItem('mindmeld_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

let _refreshPromise: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
  if (!ensureSessionEnvironment()) return null;
  if (localStorage.getItem('mindmeld_auth_method') === 'oauth') {
    const { refreshOAuthSession } = await import('./oauth');
    const session = await refreshOAuthSession();
    return session.valid ? session.token || null : null;
  }
  const refreshTok = localStorage.getItem('mindmeld_refresh_token');
  if (!refreshTok) return null;

  try {
    const response = await fetch(
      `https://cognito-idp.${COGNITO_CONFIG.region}.amazonaws.com/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
        },
        body: JSON.stringify({
          AuthFlow: 'REFRESH_TOKEN_AUTH',
          ClientId: COGNITO_CONFIG.clientId,
          AuthParameters: { REFRESH_TOKEN: refreshTok },
        }),
      }
    );

    if (!response.ok) return null;

    const data = await response.json();
    const newIdToken = data.AuthenticationResult.IdToken;
    const newAccessToken = data.AuthenticationResult.AccessToken;
    localStorage.setItem('mindmeld_token', newIdToken);
    localStorage.setItem('mindmeld_access_token', newAccessToken);
    return newIdToken;
  } catch {
    return null;
  }
}

async function ensureFreshToken(): Promise<string | null> {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = refreshToken().finally(() => { _refreshPromise = null; });
  return _refreshPromise;
}

/**
 * API fetch wrapper with auth — retries once on 401 after token refresh
 */
export const apiFetch = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data?: T; error?: string }> => {
  const doFetch = async (headers: Record<string, string>) => {
    return fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...headers, ...options.headers },
    });
  };

  try {
    let response = await doFetch(getAuthHeader());

    if (response.status === 401) {
      const newToken = await ensureFreshToken();
      if (newToken) {
        response = await doFetch({ Authorization: `Bearer ${newToken}` });
      } else {
        localStorage.removeItem('mindmeld_token');
        localStorage.removeItem('mindmeld_access_token');
        localStorage.removeItem('mindmeld_refresh_token');
        window.location.href = '/signin';
        return { success: false, error: 'Session expired' };
      }
    }

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('mindmeld_token');
        localStorage.removeItem('mindmeld_access_token');
        localStorage.removeItem('mindmeld_refresh_token');
        window.location.href = '/signin';
        return { success: false, error: 'Session expired' };
      }
      return {
        success: false,
        error: data.message || data.error || `HTTP ${response.status}`,
      };
    }

    return { success: true, data };
  } catch (error) {
    console.error('[API] Fetch error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
};
